export const deriveSuperSku = (sku) => {
    if (!sku) return '';

    const parts = sku.split('-');
    return parts.length >= 3 ? parts.slice(0, 2).join('-') : '';
};

const sanitizeGroupFilterLabel = (filterText = '') =>
    String(filterText)
        .trim()
        .replace(/^(?:[\w.]+\s+)?like\s+/i, '')
        .replace(/^['"]|['"]$/g, '')
        .replace(/[%_]+/g, ' ')
        .replace(/[^a-z0-9]+/gi, '-')
        .replace(/^-+|-+$/g, '')
        .toUpperCase();

const createUniqueGroupKey = (baseKey, existingGroupKeys = [], reservedGroupKeys = []) => {
    const cleanBaseKey = String(baseKey || '')
        .trim()
        .replace(/\s+/g, '-');
    if (!cleanBaseKey) return '';

    const reservedKeySet = new Set(reservedGroupKeys.filter(Boolean));
    const existingKeySet = new Set(
        existingGroupKeys.filter((groupKey) => groupKey && !reservedKeySet.has(groupKey)),
    );

    if (!existingKeySet.has(cleanBaseKey)) return cleanBaseKey;

    let suffix = 2;
    while (existingKeySet.has(`${cleanBaseKey}-${suffix}`)) {
        suffix += 1;
    }

    return `${cleanBaseKey}-${suffix}`;
};

export const buildFilteredGroupKey = ({
    items = [],
    filterText = '',
    existingGroupKeys = [],
}) => {
    const currentGroupKeys = Array.from(
        new Set(items.map((item) => item.superSku).filter(Boolean)),
    );
    if (currentGroupKeys.length === 1) {
        return currentGroupKeys[0];
    }

    const derivedGroups = Array.from(
        new Set(items.map((item) => deriveSuperSku(item.sku)).filter(Boolean)),
    );
    if (derivedGroups.length === 1 && items.length > 1) {
        return derivedGroups[0];
    }

    const filterLabel = sanitizeGroupFilterLabel(filterText);
    const baseKey = filterLabel ? `FILTER-${filterLabel}` : 'FILTERED-GROUP';

    return createUniqueGroupKey(baseKey, existingGroupKeys, currentGroupKeys);
};

export const buildDerivedGroupCounts = (products = []) =>
    products.reduce((counts, product) => {
        const derivedGroup = deriveSuperSku(product?.sku);
        if (!derivedGroup) return counts;

        counts.set(derivedGroup, (counts.get(derivedGroup) || 0) + 1);
        return counts;
    }, new Map());

export const getProductReviewGroup = ({
    product = null,
    selectedProducts = [],
    reviewGroupByProductId = {},
}) => {
    if (!product?.id) return '';

    if (Object.prototype.hasOwnProperty.call(reviewGroupByProductId, product.id)) {
        return reviewGroupByProductId[product.id] || '';
    }

    const derivedGroup = deriveSuperSku(product.sku);
    if (!derivedGroup) return '';

    const derivedGroupCounts = buildDerivedGroupCounts(selectedProducts);
    return derivedGroupCounts.get(derivedGroup) > 1 ? derivedGroup : '';
};

export const buildProductGenerationGroups = ({
    selectedProducts = [],
    reviewGroupByProductId = {},
    preferredProductId = '',
}) => {
    const groupsByKey = new Map();

    selectedProducts.forEach((product) => {
        const sharedGroupKey = getProductReviewGroup({
            product,
            selectedProducts,
            reviewGroupByProductId,
        });
        const groupKey = sharedGroupKey || `product:${product.id}`;

        if (!groupsByKey.has(groupKey)) {
            groupsByKey.set(groupKey, {
                groupKey,
                sharedGroupKey,
                products: [],
            });
        }

        groupsByKey.get(groupKey).products.push(product);
    });

    return Array.from(groupsByKey.values()).map((group) => ({
        ...group,
        templateProduct:
            group.products.find((product) => product.id === preferredProductId) || group.products[0],
    }));
};
